import type React from 'react';
import type { MessageAttachment } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { extensionOf, humanSize, typeLabel } from '../../../../lib/artifact-file';
import { useTranslation } from '../../../../lib/i18n';
import { attachmentAsFile } from './AttachmentGraphic';

/** The filename over what the file IS: "Markdown", "PDF · 394 KB". The second line is what the chip was
 *  missing — a name alone makes every attachment look identical until you open it. Each part is dropped
 *  when unknown rather than guessed, so a file with no recorded size simply shows its type. */
export function AttachmentLabel({ att, content, error = false }: { att: MessageAttachment; content: string; error?: boolean }) {
  const { t } = useTranslation();
  const item = attachmentAsFile(att, content);
  const type = typeLabel(extensionOf(item));
  const size = item.sizeBytes ? Number(item.sizeBytes) : null;
  const meta = [type, size && size > 0 ? humanSize(size) : null].filter(Boolean).join(' \u00b7 ');
  return (
    <span style={styles.label}>
      <span style={styles.labelName}>{att.fileName}</span>
      {error ? (
        <span style={styles.labelError}>{t('chat.attachment.openFailed')}</span>
      ) : (
        meta && <span style={styles.labelMeta}>{meta}</span>
      )}
    </span>
  );
}

const styles = {
  label: { display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, overflow: 'hidden' } satisfies React.CSSProperties,
  labelName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: 'var(--sym-weight-medium)',
  } satisfies React.CSSProperties,
  labelMeta: {
    fontSize: '0.6875rem',
    lineHeight: '0.875rem',
    color: 'rgba(var(--ink-rgb), 0.55)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  } satisfies React.CSSProperties,
  labelError: {
    fontSize: '0.6875rem',
    lineHeight: '0.875rem',
    color: 'var(--ds-text-destructive)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  } satisfies React.CSSProperties,
};
